import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Camera, CameraOff, CheckCircle2 } from 'lucide-react';

const MSG = {
  starting: 'Starting camera…',
  scanning: 'Point the camera at the asset tag',
  denied: 'Camera permission was denied. Allow camera access for this site, or use the demo tag below.',
  nocamera: 'No camera available on this device. Use the demo tag below.',
  insecure: 'Camera needs a secure (https) connection. Use the demo tag below.',
};

// Live QR scanner: native BarcodeDetector when available, jsQR fallback (iOS Safari). Stops the camera on unmount.
export function QrScanner({ onResult }) {
  const video = useRef(null);
  const canvas = useRef(null);
  const [status, setStatus] = useState('starting');
  const [hit, setHit] = useState(false);
  const cb = useRef(onResult);
  cb.current = onResult;

  useEffect(() => {
    let stream, timer, done = false;
    const stop = () => { done = true; clearTimeout(timer); if (stream) stream.getTracks().forEach((t) => t.stop()); };
    if (!window.isSecureContext) { setStatus('insecure'); return stop; }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setStatus('nocamera'); return stop; }
    const detector = 'BarcodeDetector' in window ? new window.BarcodeDetector({ formats: ['qr_code'] }) : null;

    // Recognised: buzz + green flash so the room sees the moment, then hand the tag over.
    const found = (text) => {
      if (done || !text) return;
      done = true; clearTimeout(timer);
      if (navigator.vibrate) navigator.vibrate([40, 30, 80]);
      setHit(true);
      setTimeout(() => { if (stream) stream.getTracks().forEach((t) => t.stop()); cb.current(text); }, 450);
    };
    const tick = async () => {
      if (done) return;
      const v = video.current;
      if (v && v.readyState >= 2 && v.videoWidth) {
        try {
          if (detector) {
            const codes = await detector.detect(v);
            if (codes.length) return found(codes[0].rawValue);
          } else {
            const c = canvas.current, scale = Math.min(1, 480 / v.videoWidth);
            c.width = Math.round(v.videoWidth * scale); c.height = Math.round(v.videoHeight * scale);
            const ctx = c.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(v, 0, 0, c.width, c.height);
            const img = ctx.getImageData(0, 0, c.width, c.height);
            const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
            if (code && code.data) return found(code.data);
          }
        } catch { /* keep scanning */ }
      }
      timer = setTimeout(tick, 120);
      return undefined;
    };

    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then((st) => { if (done) { st.getTracks().forEach((t) => t.stop()); return undefined; } stream = st; video.current.srcObject = st; return video.current.play(); })
      .then(() => { if (!done) { setStatus('scanning'); tick(); } })
      .catch((e) => setStatus(e && e.name === 'NotAllowedError' ? 'denied' : 'nocamera'));
    return stop;
  }, []);

  const live = status === 'starting' || status === 'scanning';
  return (
    <div className="fl-cam" data-testid="fl-qr-camera" data-status={status}>
      <div className={`fl-cam-view ${live ? '' : 'fl-cam-off'}`}>
        <video ref={video} playsInline muted autoPlay className="fl-cam-video" data-testid="fl-qr-video" />
        <canvas ref={canvas} hidden />
        {live ? <div className="fl-cam-reticle" aria-hidden="true" /> : <CameraOff size={36} className="fl-muted" />}
        {hit && <div className="fl-cam-hit" data-testid="fl-qr-hit" role="status"><CheckCircle2 size={56} /></div>}
      </div>
      <p className={`fl-small ${live ? '' : 'fl-cam-msg'}`} data-testid="fl-qr-status"><Camera size={12} /> {hit ? 'Tag recognised' : MSG[status]}</p>
    </div>
  );
}
